import { useCallback, useEffect, useRef, useState } from "react";
import {
  getChainTestLists,
  getChainMemberLabs,
  getChainTestList,
} from "../../../services/diagnosticLabsService";
import { isProcessing } from "./ui";

// Server state for the Chain Test Lists workspace: the chain's lists and its
// member labs (with the test source in effect). Mutations call refresh()/
// replaceList() afterwards — there is no optimistic state to reconcile.
export const useChainTestLists = (role, chainId) => {
  const [chain, setChain] = useState(null);
  const [lists, setLists] = useState([]);
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const abortRef = useRef(null);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    if (!silent) setLoading(true);
    setError("");
    try {
      const [listData, labData] = await Promise.all([
        getChainTestLists(role, chainId, { signal: controller.signal }),
        getChainMemberLabs(role, chainId, { signal: controller.signal }),
      ]);
      setChain(listData?.chain || null);
      setLists(listData?.lists || []);
      setLabs(labData?.items || []);
    } catch (err) {
      if (err.name !== "AbortError") setError(err.message || "Failed to load test lists.");
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [role, chainId]);

  useEffect(() => {
    refresh();
    return () => abortRef.current?.abort();
  }, [refresh]);

  // Swap in a freshly fetched list (e.g. from polling) without refetching everything.
  const replaceList = useCallback((list) => {
    setLists((prev) => {
      const exists = prev.some((l) => l.id === list.id);
      if (!list.active) return prev.filter((l) => l.id !== list.id); // contract: loan-and-subscription/internalapi/views/user_type/shared/diagnostic/chain/chain_test_list_serializers.py serialize_test_list 'active'
      return exists ? prev.map((l) => (l.id === list.id ? { ...l, ...list } : l)) : [...prev, list];
    });
  }, []);

  return { chain, lists, labs, loading, error, refresh, replaceList };
};

// Polls every list whose latest upload is still queued/processing, backing off
// 2s → 5s, until it reaches a terminal status. Resumes naturally after a page
// reload because it is driven by server state, not by the upload action.
export const useUploadPolling = (role, chainId, lists, onUpdate, onSettled) => {
  const timers = useRef({});
  const attempts = useRef({});
  const pendingIds = lists.filter((l) => isProcessing(l.latest_upload)).map((l) => l.id).join(",");

  useEffect(() => {
    const ids = pendingIds ? pendingIds.split(",") : [];
    let cancelled = false;

    const poll = async (listId) => {
      try {
        const fresh = await getChainTestList(role, chainId, listId);
        if (cancelled || !fresh) return;
        onUpdate(fresh);
        if (!isProcessing(fresh.latest_upload)) {
          delete attempts.current[listId];
          onSettled?.(fresh);
          return;
        }
      } catch { /* transient — keep polling */ }
      if (cancelled) return;
      const n = (attempts.current[listId] = (attempts.current[listId] || 0) + 1);
      timers.current[listId] = setTimeout(() => poll(listId), n < 5 ? 2000 : 5000);
    };

    ids.forEach((id) => {
      timers.current[id] = setTimeout(() => poll(id), 1500);
    });
    return () => {
      cancelled = true;
      Object.values(timers.current).forEach(clearTimeout);
      timers.current = {};
    };
    // onUpdate/onSettled are stable callbacks from the page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingIds, role, chainId]);
};
